import React, { useState, useRef, useEffect } from 'react';
import './VideoComparison.css';

const VideoComparison = ({ jobId, originalFilename, onClose, comparisonMode = 'original' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [thumbnailTime, setThumbnailTime] = useState(null);
  const [thumbnailPosition, setThumbnailPosition] = useState(0);
  const [strokeData, setStrokeData] = useState(null);
  const [currentStroke, setCurrentStroke] = useState(null);

  // Reference mode state
  const [referenceMetadata, setReferenceMetadata] = useState(null);
  const [selectedAngle, setSelectedAngle] = useState('side-above');
  const [referenceAvailable, setReferenceAvailable] = useState(false);

  const originalVideoRef = useRef(null);
  const processedVideoRef = useRef(null);
  const progressRef = useRef(null);
  const thumbnailVideoRef = useRef(null);

  const API_BASE_URL = '/api';

  // Load reference video metadata when in reference mode
  useEffect(() => {
    const loadReferenceMetadata = async () => {
      try {
        const response = await fetch('/reference-videos/metadata.json');
        if (response.ok) {
          const data = await response.json();
          setReferenceMetadata(data);

          // Check if selected angle has available video
          const freestyle = data.strokes?.freestyle;
          if (freestyle?.angles?.[selectedAngle]) {
            setReferenceAvailable(freestyle.angles[selectedAngle].available);
          }
        }
      } catch (error) {
        console.error('Failed to load reference metadata:', error);
      }
    };

    if (comparisonMode === 'reference') {
      loadReferenceMetadata();
    }
  }, [comparisonMode, selectedAngle]);

  // Load stroke analysis data
  useEffect(() => {
    const loadStrokeData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/download/${jobId}?file_type=json`);
        if (response.ok) {
          const data = await response.json();
          setStrokeData(data);
        }
      } catch (error) {
        console.error('Failed to load stroke data:', error);
      }
    };

    if (jobId) {
      loadStrokeData();
    }
  }, [jobId]);

  // Update current stroke based on video time
  useEffect(() => {
    if (strokeData && strokeData.detailed_data) {
      const currentFrame = strokeData.detailed_data.find(frame => 
        Math.abs(frame.timestamp - currentTime) < 0.1
      );
      
      if (currentFrame && currentFrame.analysis?.stroke_classification) {
        setCurrentStroke(currentFrame.analysis.stroke_classification);
      } else {
        setCurrentStroke(null);
      }
    }
  }, [currentTime, strokeData]);

  useEffect(() => {
    // Set up video event listeners for synchronization
    const originalVideo = originalVideoRef.current;
    const processedVideo = processedVideoRef.current;

    if (originalVideo && processedVideo) {
      const handleTimeUpdate = () => {
        setCurrentTime(originalVideo.currentTime);
        // Sync the processed video to the same time
        if (Math.abs(processedVideo.currentTime - originalVideo.currentTime) > 0.1) {
          processedVideo.currentTime = originalVideo.currentTime;
        }
      };

      const handleLoadedMetadata = () => {
        console.log('Original video loaded, duration:', originalVideo.duration);
        setDuration(originalVideo.duration);
        setIsLoading(false);
      };

      const handlePlay = () => {
        console.log('Video play event');
        setIsPlaying(true);
      };
      
      const handlePause = () => {
        console.log('Video pause event');
        setIsPlaying(false);
      };

      const handleEnded = () => {
        console.log('Video ended');
        setIsPlaying(false);
        setCurrentTime(0);
        originalVideo.currentTime = 0;
        processedVideo.currentTime = 0;
      };

      originalVideo.addEventListener('timeupdate', handleTimeUpdate);
      originalVideo.addEventListener('loadedmetadata', handleLoadedMetadata);
      originalVideo.addEventListener('play', handlePlay);
      originalVideo.addEventListener('pause', handlePause);
      originalVideo.addEventListener('ended', handleEnded);

      // Also set up event listeners for processed video
      processedVideo.addEventListener('loadedmetadata', () => {
        console.log('Processed video loaded, duration:', processedVideo.duration);
      });

      return () => {
        originalVideo.removeEventListener('timeupdate', handleTimeUpdate);
        originalVideo.removeEventListener('loadedmetadata', handleLoadedMetadata);
        originalVideo.removeEventListener('play', handlePlay);
        originalVideo.removeEventListener('pause', handlePause);
        originalVideo.removeEventListener('ended', handleEnded);
      };
    }
  }, [jobId]); // Add jobId as dependency to prevent unnecessary re-renders

  // Handle thumbnail video seeking when thumbnailTime changes
  useEffect(() => {
    if (thumbnailVideoRef.current && thumbnailTime !== null && !isNaN(thumbnailTime)) {
      thumbnailVideoRef.current.currentTime = thumbnailTime;
    }
  }, [thumbnailTime]);

  const togglePlay = async () => {
    const originalVideo = originalVideoRef.current;
    const processedVideo = processedVideoRef.current;

    if (!originalVideo || !processedVideo) {
      console.error('Videos not loaded');
      return;
    }

    try {
      if (isPlaying) {
        console.log('Pausing videos');
        originalVideo.pause();
        processedVideo.pause();
      } else {
        console.log('Playing videos');
        // Ensure both videos are at the same time before playing
        if (Math.abs(originalVideo.currentTime - processedVideo.currentTime) > 0.1) {
          processedVideo.currentTime = originalVideo.currentTime;
        }
        
        // Play both videos
        const originalPromise = originalVideo.play();
        const processedPromise = processedVideo.play();
        
        await Promise.all([originalPromise, processedPromise]);
        console.log('Both videos started playing');
      }
    } catch (error) {
      console.error('Error toggling play:', error);
      setError('Failed to play videos: ' + error.message);
    }
  };

  const handleSeek = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * duration;

    const originalVideo = originalVideoRef.current;
    const processedVideo = processedVideoRef.current;

    if (originalVideo && processedVideo) {
      console.log('Seeking to:', newTime);
      originalVideo.currentTime = newTime;
      processedVideo.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressHover = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, hoverX / rect.width));
    const hoverTime = percentage * duration;
    
    setThumbnailTime(hoverTime);
    setThumbnailPosition(percentage * 100);
    
    // Seek the thumbnail video to the correct time
    if (thumbnailVideoRef.current && !isNaN(hoverTime)) {
      thumbnailVideoRef.current.currentTime = hoverTime;
    }
    
    const tooltip = e.currentTarget.parentElement;
    tooltip.setAttribute('data-tooltip', formatTime(hoverTime));
  };

  const handleProgressLeave = () => {
    setThumbnailTime(null);
  };



  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Generate URLs once and memoize them
  const originalVideoUrl = React.useMemo(() =>
    `${API_BASE_URL}/download/${jobId}?file_type=original`,
    [jobId]
  );
  const processedVideoUrl = React.useMemo(() =>
    `${API_BASE_URL}/download/${jobId}?file_type=video`,
    [jobId]
  );

  // Reference video URL (for reference mode)
  const referenceVideoUrl = React.useMemo(() => {
    if (comparisonMode === 'reference' && referenceMetadata) {
      const angleData = referenceMetadata.strokes?.freestyle?.angles?.[selectedAngle];
      if (angleData?.file) {
        return `/reference-videos/freestyle/${angleData.file}`;
      }
    }
    return null;
  }, [comparisonMode, referenceMetadata, selectedAngle]);

  // Get available angles for the selector
  const availableAngles = React.useMemo(() => {
    if (referenceMetadata?.strokes?.freestyle?.angles) {
      return Object.entries(referenceMetadata.strokes.freestyle.angles).map(([key, value]) => ({
        id: key,
        name: value.name,
        description: value.description,
        available: value.available,
        keyTechniques: value.keyTechniques
      }));
    }
    return [];
  }, [referenceMetadata]);

  console.log('Video URLs:', { originalVideoUrl, processedVideoUrl, referenceVideoUrl });

  // Determine which video to show in the second panel
  const secondVideoUrl = comparisonMode === 'reference' ? referenceVideoUrl : processedVideoUrl;
  const isReferenceMode = comparisonMode === 'reference';

  return (
    <div className="video-comparison-overlay">
      <div className="video-comparison-container">
        <div className="video-comparison-header">
          <h2>{isReferenceMode ? 'Compare to Ideal Technique' : 'Video Comparison'}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="video-comparison-content">
          {/* Angle Selector for Reference Mode */}
          {isReferenceMode && (
            <div className="angle-selector-container">
              <div className="angle-selector-header">
                <span className="angle-selector-icon">📐</span>
                <span className="angle-selector-title">Select Camera Angle</span>
              </div>
              <div className="angle-selector-grid">
                {availableAngles.map((angle) => (
                  <button
                    key={angle.id}
                    className={`angle-option ${selectedAngle === angle.id ? 'selected' : ''} ${!angle.available ? 'unavailable' : ''}`}
                    onClick={() => setSelectedAngle(angle.id)}
                  >
                    <div className="angle-option-name">{angle.name}</div>
                    <div className="angle-option-status">
                      {angle.available ? (
                        <span className="status-available">Available</span>
                      ) : (
                        <span className="status-coming-soon">Coming Soon</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {selectedAngle && availableAngles.find(a => a.id === selectedAngle) && (
                <div className="angle-info">
                  <p className="angle-description">
                    {availableAngles.find(a => a.id === selectedAngle)?.description}
                  </p>
                  <div className="key-techniques">
                    <span className="key-techniques-label">Key techniques visible:</span>
                    <div className="technique-tags">
                      {availableAngles.find(a => a.id === selectedAngle)?.keyTechniques?.map((tech, idx) => (
                        <span key={idx} className="technique-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="video-grid">
            <div className="video-container">
              <h3>{isReferenceMode ? 'Your Video' : 'Original Video (720p)'}</h3>
                             <video
                 key={`original-${jobId}`}
                 ref={originalVideoRef}
                 src={originalVideoUrl}
                 controls={false}
                 muted={true}
                 preload="metadata"
                onError={(e) => {
                  console.error('Original video error:', e);
                  console.error('Original video error details:', {
                    error: e.target.error,
                    networkState: e.target.networkState,
                    readyState: e.target.readyState,
                    src: e.target.src
                  });
                  setError('Failed to load original video');
                }}
                onLoadStart={() => console.log('Original video load started')}
                onCanPlay={() => console.log('Original video can play')}
                onPlay={() => console.log('Original video play event')}
                onPause={() => console.log('Original video pause event')}
                onTimeUpdate={() => console.log('Original video time update:', originalVideoRef.current?.currentTime)}
                onLoadedData={() => console.log('Original video loaded data')}
                onCanPlayThrough={() => console.log('Original video can play through')}
                onStalled={() => console.log('Original video stalled')}
                onSuspend={() => console.log('Original video suspended')}
                onAbort={() => console.log('Original video aborted')}
                onEmptied={() => console.log('Original video emptied')}
                onWaiting={() => console.log('Original video waiting')}
              />
            </div>
            <div className="video-container">
              <h3>{isReferenceMode ? 'Ideal Technique' : 'Processed Video (720p)'}</h3>
              {isReferenceMode && !referenceAvailable ? (
                <div className="reference-placeholder">
                  <div className="placeholder-icon">🎬</div>
                  <div className="placeholder-title">Reference Video Coming Soon</div>
                  <div className="placeholder-description">
                    AI-generated ideal freestyle technique video for this angle is in development.
                  </div>
                  <div className="placeholder-note">
                    In the meantime, you can compare your original and processed videos using the standard comparison mode.
                  </div>
                </div>
              ) : (
                <video
                  key={isReferenceMode ? `reference-${selectedAngle}` : `processed-${jobId}`}
                  ref={processedVideoRef}
                  src={secondVideoUrl}
                  controls={false}
                  muted={true}
                  preload="metadata"
                  onError={(e) => {
                    console.error('Second video error:', e);
                    console.error('Second video error details:', {
                      error: e.target.error,
                      networkState: e.target.networkState,
                      readyState: e.target.readyState,
                      src: e.target.src
                    });
                    setError(isReferenceMode ? 'Failed to load reference video' : 'Failed to load processed video');
                  }}
                  onLoadStart={() => console.log('Second video load started')}
                  onCanPlay={() => console.log('Second video can play')}
                  onPlay={() => console.log('Second video play event')}
                  onPause={() => console.log('Second video pause event')}
                  onTimeUpdate={() => console.log('Second video time update:', processedVideoRef.current?.currentTime)}
                />
              )}
            </div>
          </div>

          {error && (
            <div className="error-message">
              <span>❌</span>
              <span>{error}</span>
            </div>
          )}

          {isLoading && (
            <div className="loading-message">
              <div className="loading-spinner"></div>
              <span>Loading videos...</span>
            </div>
          )}

          <div className="video-controls">
                         <div className="control-buttons">
               <button 
                 className="play-button" 
                 onClick={togglePlay}
                 disabled={isLoading}
               >
                 {isPlaying ? '⏸️' : '▶️'}
               </button>
             </div>

                                                   <div className="progress-container">
                <div 
                  className="progress-bar" 
                  ref={progressRef}
                  onClick={handleSeek}
                  onMouseMove={handleProgressHover}
                  onMouseLeave={handleProgressLeave}
                >
                  <div 
                    className="progress-fill"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                  
                  {/* Thumbnail Preview */}
                  {thumbnailTime !== null && (
                    <div 
                      className="thumbnail-preview"
                      style={{ left: `${thumbnailPosition}%` }}
                    >
                                             <div className="thumbnail-video-container">
                         <video
                           ref={thumbnailVideoRef}
                           src={originalVideoUrl}
                           muted={true}
                           preload="metadata"
                           style={{ 
                             width: '120px', 
                             height: '67px',
                             objectFit: 'cover'
                           }}
                         />
                       </div>
                      <div className="thumbnail-time">
                        {formatTime(thumbnailTime)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

            
          </div>

          <div className="comparison-info">
            <div className="info-card">
              <h4>{isReferenceMode ? '📹 Your Video' : '📹 Original Video (720p)'}</h4>
              <p>{isReferenceMode
                ? 'Your analyzed swim video to compare against ideal technique'
                : 'Your uploaded swim video resized to 720p for consistent comparison'
              }</p>
            </div>
            <div className="info-card">
              <h4>{isReferenceMode ? '🏆 Ideal Technique' : '🤖 Processed Video (720p)'}</h4>
              <p>{isReferenceMode
                ? 'AI-generated reference showing biomechanically correct freestyle technique'
                : 'AI-enhanced video with pose detection and analysis overlays'
              }</p>
            </div>
          </div>

          {/* Real-time Stroke Display - Only show when videos are loaded and not in error state - FIXED */}
          {currentStroke && !isLoading && !error && (
            <div className="stroke-display">
              <div className="stroke-display-header">
                <h4>🏊‍♂️ Current Stroke Analysis</h4>
              </div>
              <div className="stroke-display-content">
                <div className="stroke-info">
                  <div className="stroke-name">
                    {currentStroke.primary_stroke?.charAt(0).toUpperCase() + currentStroke.primary_stroke?.slice(1) || 'Unknown'}
                  </div>
                  <div className="stroke-confidence">
                    Confidence: {Math.round((currentStroke.confidence || 0) * 100)}%
                  </div>
                </div>
                {currentStroke.alternative_strokes && currentStroke.alternative_strokes.length > 0 && (
                  <div className="alternative-strokes">
                    <div className="alt-strokes-label">Alternative strokes:</div>
                    <div className="alt-strokes-list">
                      {currentStroke.alternative_strokes.map((stroke, index) => (
                        <span key={index} className="alt-stroke">
                          {stroke.charAt(0).toUpperCase() + stroke.slice(1)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoComparison; 